import React, { useState, useEffect, useCallback } from "react";
import {
    Database,
    Settings,
    RefreshCw,
    Save,
    Eye,
    EyeOff,
    AlertCircle,
    CheckCircle,
    Server,
    Globe,
    Download,
} from "lucide-react";
import { db, auth } from "../../firebase";
import {
    doc,
    getDoc,
    setDoc,
} from "firebase/firestore";

const LivePF = () => {
    // Estados para configuración
    const [showConfig, setShowConfig] = useState(false);
    const [mongoUri, setMongoUri] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);

    // Estados para datos
    const [pfData, setPfData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);

    // Estados de navegación
    const [databases, setDatabases] = useState([]);
    const [selectedDatabase, setSelectedDatabase] = useState("");
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState("");

    // Estados de conexión
    const [isConnected, setIsConnected] = useState(false);
    const [limit, setLimit] = useState(100);

    // Estados para análisis de estructura
    const [analyzingStructure, setAnalyzingStructure] = useState(false);
    const [dbStructures, setDbStructures] = useState({});

    // Firebase Functions URL base
    const FUNCTIONS_BASE_URL = "https://us-central1-newpollofeliz.cloudfunctions.net";

    // Notificación
    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Obtener token de autenticación
    const getAuthToken = async () => {
        const user = auth.currentUser;
        if (!user) {
            throw new Error("No hay usuario autenticado");
        }
        return await user.getIdToken();
    };

    // Cargar configuración desde Firebase usando Cloud Function
    const loadConfig = useCallback(async () => {
        try {
            const token = await getAuthToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/getMongoConfig`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();

            if (data.success) {
                setMongoUri(data.mongoUri || "");
                if (data.mongoUri) {
                    setIsConnected(true);
                }
            } else {
                // Configuración inicial por defecto (URI completa de Compass)
                setMongoUri("mongodb+srv://pf_pedro_app:C5T8EqMgvo2SHRGn@cluster0.9d4jfhr.mongodb.net/?retryWrites=true&w=majority&authSource=admin");
            }
        } catch (error) {
            console.error("Error cargando configuración:", error);
            // Si hay error, usar configuración por defecto
            setMongoUri("mongodb+srv://pf_pedro_app:C5T8EqMgvo2SHRGn@cluster0.9d4jfhr.mongodb.net/?retryWrites=true&w=majority&authSource=admin");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cargar configuración y conectar automáticamente al montar
    useEffect(() => {
        const initializeLivePF = async () => {
            await loadConfig();
            // Esperar un poco para que se cargue la URI
            setTimeout(() => {
                if (mongoUri) {
                    fetchDatabases();
                }
            }, 500);
        };

        initializeLivePF();
    }, [loadConfig]);

    // Auto-conectar cuando la URI cambie
    useEffect(() => {
        if (mongoUri && !isConnected) {
            fetchDatabases();
        }
    }, [mongoUri]);

    // Guardar configuración en Firebase usando Cloud Function
    const saveConfig = async () => {
        if (!mongoUri) {
            showNotification("error", "La URI de conexión es obligatoria");
            return;
        }

        if (!mongoUri.startsWith("mongodb://") && !mongoUri.startsWith("mongodb+srv://")) {
            showNotification("error", "La URI debe empezar con mongodb:// o mongodb+srv://");
            return;
        }

        setSavingConfig(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/saveMongoConfig`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    mongoUri: mongoUri,
                }),
            });

            const data = await response.json();

            if (data.success) {
                showNotification("success", "✅ Configuración guardada correctamente");
                setShowConfig(false);
                setIsConnected(true);
            } else {
                showNotification("error", data.error || "Error al guardar");
            }
        } catch (error) {
            console.error("Error guardando configuración:", error);
            showNotification("error", "Error al guardar la configuración");
        } finally {
            setSavingConfig(false);
        }
    };

    // Conectar y obtener bases de datos disponibles usando Firebase Function
    const fetchDatabases = async () => {
        setLoading(true);
        setDatabases([]);
        setSelectedDatabase("");
        setCollections([]);
        setSelectedCollection("");
        setPfData([]);

        try {
            const token = await getAuthToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/listDatabases`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uri: mongoUri,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setDatabases(data.databases);
                showNotification("success", `✅ Conectado. ${data.totalDatabases} bases de datos encontradas`);
            } else {
                showNotification("error", data.error || "Error al conectar");
            }
        } catch (error) {
            console.error("Error obteniendo bases de datos:", error);
            showNotification("error", "❌ Error al conectar. Verifica tu conexión y permisos");
        } finally {
            setLoading(false);
        }
    };

    // Obtener colecciones de una base de datos usando Firebase Function
    const fetchCollections = async () => {
        if (!selectedDatabase) {
            showNotification("error", "Selecciona una base de datos primero");
            return;
        }

        setLoading(true);
        setCollections([]);
        setSelectedCollection("");
        setPfData([]);

        try {
            const token = await getAuthToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/listCollections`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    database: selectedDatabase,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setCollections(data.collections);
                showNotification("success", `📋 ${data.totalCollections} colecciones encontradas en ${selectedDatabase}`);
            } else {
                showNotification("error", data.error || "Error al obtener colecciones");
            }
        } catch (error) {
            console.error("Error obteniendo colecciones:", error);
            showNotification("error", "Error al conectar. Verifica tu conexión y permisos");
        } finally {
            setLoading(false);
        }
    };

    // Obtener datos de una colección usando Firebase Function
    const fetchData = async () => {
        if (!selectedDatabase) {
            showNotification("error", "Selecciona una base de datos");
            return;
        }

        if (!selectedCollection) {
            showNotification("error", "Selecciona una colección");
            return;
        }

        setLoading(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/getData`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    database: selectedDatabase,
                    collection: selectedCollection,
                    limit: limit,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setPfData(data.data);
                showNotification("success", `📊 ${data.count} de ${data.totalCount} registros obtenidos de ${selectedCollection}`);
            } else {
                showNotification("error", data.error || "Error al obtener datos");
            }
        } catch (error) {
            console.error("Error obteniendo datos:", error);
            showNotification("error", "Error al conectar. Verifica tu conexión y permisos");
        } finally {
            setLoading(false);
        }
    };

    // Analizar estructura de una base de datos
    const analyzeDatabase = async (dbName) => {
        setAnalyzingStructure(true);
        try {
            const token = await getAuthToken();

            // Primero obtener las colecciones
            const collectionsResponse = await fetch(`${FUNCTIONS_BASE_URL}/listCollections`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    database: dbName,
                }),
            });

            const collectionsData = await collectionsResponse.json();

            if (!collectionsData.success) {
                showNotification("error", "Error al analizar base de datos");
                return;
            }

            // Analizar cada colección (obtener 5 documentos de ejemplo)
            const structure = {};

            for (const collection of collectionsData.collections) {
                try {
                    const dataResponse = await fetch(`${FUNCTIONS_BASE_URL}/getData`, {
                        method: "POST",
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            database: dbName,
                            collection: collection.name,
                            limit: 5,
                        }),
                    });

                    const dataResult = await dataResponse.json();

                    if (dataResult.success && dataResult.data.length > 0) {
                        // Analizar campos del primer documento
                        const sampleDoc = dataResult.data[0];
                        const fields = {};

                        Object.keys(sampleDoc).forEach(key => {
                            const value = sampleDoc[key];
                            fields[key] = {
                                type: Array.isArray(value) ? 'array' : typeof value,
                                example: JSON.stringify(value).substring(0, 50),
                            };
                        });

                        structure[collection.name] = {
                            totalCount: dataResult.totalCount,
                            fields: fields,
                            sampleData: dataResult.data,
                        };
                    } else {
                        structure[collection.name] = {
                            totalCount: 0,
                            fields: {},
                            sampleData: [],
                        };
                    }
                } catch (error) {
                    console.error(`Error analizando ${collection.name}:`, error);
                }
            }

            setDbStructures(prev => ({
                ...prev,
                [dbName]: structure,
            }));

            showNotification("success", `✅ Estructura de ${dbName} analizada`);
        } catch (error) {
            console.error("Error analizando estructura:", error);
            showNotification("error", "Error al analizar estructura");
        } finally {
            setAnalyzingStructure(false);
        }
    };

    // Exportar datos a JSON
    const exportToJson = () => {
        if (pfData.length === 0) {
            showNotification("error", "No hay datos para exportar");
            return;
        }

        const dataStr = JSON.stringify(pfData, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `pf_${selectedCollection}_${new Date().toISOString().split("T")[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);

        showNotification("success", "Datos exportados correctamente");
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Database className="w-8 h-8 text-[#FF0000]" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">LivePF</h1>
                            <p className="text-gray-600 mt-1">
                                Conexión en vivo con MongoDB Atlas
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${isConnected ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'}`}>
                            <Server className="w-4 h-4" />
                            <span className="text-sm font-medium">
                                {isConnected ? "Configurado" : "Sin configurar"}
                            </span>
                        </div>
                        <button
                            onClick={() => setShowConfig(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <Settings className="w-4 h-4" />
                            Configurar
                        </button>
                    </div>
                </div>
            </div>

            {/* Notificación */}
            {notification && (
                <div
                    className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${notification.type === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : notification.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-blue-50 border border-blue-200 text-blue-800"
                        }`}
                >
                    {notification.type === "success" ? (
                        <CheckCircle className="w-5 h-5" />
                    ) : (
                        <AlertCircle className="w-5 h-5" />
                    )}
                    <span>{notification.message}</span>
                </div>
            )}

            {/* Advertencia de Backend */}
            <div className="mb-6 p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold text-yellow-900 mb-2">
                            ⚠️ Backend Requerido
                        </h3>
                        <p className="text-sm text-yellow-800 mb-2">
                            Para conectarte a MongoDB, necesitas ejecutar el servidor backend:
                        </p>
                        <div className="bg-yellow-100 rounded p-3 font-mono text-xs text-yellow-900 mb-2">
                            cd server && npm install && npm start
                        </div>
                        <p className="text-xs text-yellow-700">
                            El servidor debe estar corriendo en <code>http://localhost:5000</code>
                        </p>
                    </div>
                </div>
            </div>

            {/* Paso 1: Conectar */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Paso 1: Conectar al Cluster</h3>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${databases.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {databases.length > 0 ? '✓ Conectado' : 'Sin conectar'}
                    </div>
                </div>
                <button
                    onClick={fetchDatabases}
                    disabled={loading || !isConnected}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors disabled:opacity-50 font-medium"
                >
                    {loading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Conectando...
                        </>
                    ) : (
                        <>
                            <Database className="w-5 h-5" />
                            Conectar y Listar Bases de Datos
                        </>
                    )}
                </button>
            </div>

            {/* Paso 2: Seleccionar Base de Datos */}
            {databases.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Paso 2: Seleccionar Base de Datos</h3>
                        <span className="text-sm text-gray-600">{databases.length} disponibles</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {databases.map((database) => (
                            <button
                                key={database.name}
                                onClick={() => {
                                    setSelectedDatabase(database.name);
                                    setCollections([]);
                                    setSelectedCollection("");
                                    setPfData([]);
                                }}
                                className={`p-4 rounded-lg border-2 transition-all ${selectedDatabase === database.name
                                    ? 'border-[#FF0000] bg-red-50 text-[#FF0000]'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                    }`}
                            >
                                <Database className="w-6 h-6 mx-auto mb-2" />
                                <p className="text-sm font-medium truncate">{database.name}</p>
                            </button>
                        ))}
                    </div>
                    {selectedDatabase && (
                        <button
                            onClick={fetchCollections}
                            disabled={loading}
                            className="w-full mt-4 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
                        >
                            <RefreshCw className="w-5 h-5" />
                            Ver Colecciones de {selectedDatabase}
                        </button>
                    )}
                </div>
            )}

            {/* Paso 3: Seleccionar Colección y Ver Datos */}
            {collections.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Paso 3: Seleccionar Colección</h3>
                        <span className="text-sm text-gray-600">{collections.length} colecciones</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Colección
                            </label>
                            <select
                                value={selectedCollection}
                                onChange={(e) => {
                                    setSelectedCollection(e.target.value);
                                    setPfData([]);
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                            >
                                <option value="">Selecciona una colección</option>
                                {collections.map((col) => (
                                    <option key={col.name} value={col.name}>
                                        {col.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Límite de registros
                            </label>
                            <select
                                value={limit}
                                onChange={(e) => setLimit(Number(e.target.value))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                            >
                                <option value={10}>10 registros</option>
                                <option value={50}>50 registros</option>
                                <option value={100}>100 registros</option>
                                <option value={500}>500 registros</option>
                                <option value={1000}>1000 registros</option>
                            </select>
                        </div>
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading || !selectedCollection}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Obteniendo datos...
                            </>
                        ) : (
                            <>
                                <Eye className="w-5 h-5" />
                                Ver Datos
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Datos */}
            {pfData.length > 0 && (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {selectedDatabase} → {selectedCollection}
                            </h3>
                            <p className="text-sm text-gray-600">
                                {pfData.length} registros (solo lectura)
                            </p>
                        </div>
                        <button
                            onClick={exportToJson}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Exportar JSON
                        </button>
                    </div>

                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                        <pre className="p-6 text-xs bg-gray-50 text-gray-800 font-mono">
                            {JSON.stringify(pfData, null, 2)}
                        </pre>
                    </div>
                </div>
            )}

            {/* Modal de Configuración */}
            {showConfig && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden">
                        <div className="p-6 border-b border-gray-200 bg-gray-50">
                            <div className="flex items-center gap-3">
                                <Settings className="w-6 h-6 text-[#FF0000]" />
                                <h3 className="text-xl font-bold text-gray-900">
                                    Configuración de MongoDB
                                </h3>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                    <Globe className="w-4 h-4" />
                                    URI de Conexión MongoDB (de Compass)
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={mongoUri}
                                        onChange={(e) => setMongoUri(e.target.value)}
                                        placeholder="mongodb+srv://usuario:contraseña@cluster0.xxxxx.mongodb.net/..."
                                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000] font-mono text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    💡 Copia la URI completa desde MongoDB Compass (incluye usuario, contraseña y parámetros)
                                </p>
                            </div>

                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800 mb-2">
                                    <strong>🔒 Solo Lectura:</strong> Este módulo solo permite consultar datos.
                                    No se pueden crear, editar ni eliminar registros.
                                </p>
                                <p className="text-xs text-blue-700">
                                    La URI se guarda de forma segura en Firebase Firestore.
                                </p>
                            </div>

                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-xs font-mono text-gray-700 break-all">
                                    mongodb+srv://usuario:contraseña@cluster0.9d4jfhr.mongodb.net/?retryWrites=true&w=majority&authSource=admin
                                </p>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => setShowConfig(false)}
                                disabled={savingConfig}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={saveConfig}
                                disabled={savingConfig}
                                className="flex items-center gap-2 px-6 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors disabled:opacity-50"
                            >
                                {savingConfig ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Guardar Configuración
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LivePF;

